// Quick Connection Test Script
// Run this directly in browser console or import in a component

const QuickConnectionTest = {
  // Main test runner
  async runAll() {
    console.clear();
    console.log('%c🔍 MoveMend Connection Test Starting...', 'font-size: 20px; color: blue; font-weight: bold');
    console.log('='.repeat(60));
    
    const results = {
      timestamp: new Date().toISOString(),
      frontend: this.testFrontend(),
      auth: this.testAuth(),
      localStorage: this.testLocalStorage(),
      supabase: await this.testSupabase(),
      hume: this.testHume(),
      routing: this.testRouting(),
      mockData: this.testMockData()
    };
    
    this.printSummary(results);
    return results;
  },

  // Test frontend components
  testFrontend() {
    const tests = {
      react: typeof React !== 'undefined',
      dashboardsLoaded: {
        patient: !!document.querySelector('.patient-dashboard-section'),
        clinician: !!document.querySelector('.clinician-dashboard-section, .clinician-dashboard'),
        superadmin: !!document.querySelector('.superadmin-dashboard-section, .super-admin-dashboard')
      },
      uiComponents: {
        buttons: document.querySelectorAll('button').length > 0,
        inputs: document.querySelectorAll('input').length > 0
      }
    };
    
    console.log('\n📱 FRONTEND:', tests.react ? '✅ React Loaded' : '❌ React Not Found');
    console.log('  Dashboards:', 
      Object.entries(tests.dashboardsLoaded)
        .map(([k,v]) => `${k}: ${v ? '✅' : '❌'}`)
        .join(', ')
    );
    
    return tests;
  },

  // Test authentication status
  testAuth() {
    const user = localStorage.getItem('user');
    const authBypassed = window.location.pathname.includes('dashboard') && !document.querySelector('input[type="password"]');
    
    let result = {
      hasUser: !!user,
      authBypassed,
      userData: null,
      mode: 'unknown'
    };
    
    if (user) {
      try {
        result.userData = JSON.parse(user);
        result.mode = result.userData.role || result.userData.user_type || 'unknown';
      } catch (e) {
        result.mode = 'invalid';
      }
    }
    
    console.log('\n🔐 AUTHENTICATION:');
    console.log(`  Status: ${result.authBypassed ? '⚠️ BYPASSED (Testing Mode)' : result.hasUser ? '✅ Logged In' : '❌ Not Logged In'}`);
    if (result.userData) {
      console.log(`  User: ${result.userData.name || 'Unknown'} (${result.mode})`);
    }
    
    return result;
  },

  // Test localStorage
  testLocalStorage() {
    try {
      const testKey = 'movemend_test_' + Date.now();
      localStorage.setItem(testKey, 'test');
      const works = localStorage.getItem(testKey) === 'test';
      localStorage.removeItem(testKey);
      
      const hasData = {
        user: !!localStorage.getItem('user'),
        hume: !!localStorage.getItem('hume_voice_active'),
        other: Object.keys(localStorage).filter(k => k.startsWith('movemend_')).length
      };
      
      console.log('\n💾 LOCAL STORAGE:', works ? '✅ Working' : '❌ Not Working');
      console.log(`  Data: User=${hasData.user ? '✅' : '❌'}, Hume=${hasData.hume ? '✅' : '❌'}, Others=${hasData.other}`);
      
      return { works, hasData };
    } catch (e) {
      console.log('\n💾 LOCAL STORAGE: ❌ Error -', e.message);
      return { works: false, error: e.message };
    }
  },

  // Test Supabase connection
  async testSupabase() {
    const url = process.env.REACT_APP_SUPABASE_URL;
    const key = process.env.REACT_APP_SUPABASE_KEY;
    
    console.log('\n🗄️ SUPABASE:');
    
    if (!url || !key) {
      console.log('  ❌ Not Configured (Missing credentials in .env)');
      console.log('  Tip: Restart app after adding .env variables');
      return { configured: false };
    }
    
    console.log('  ✅ Credentials Found');
    
    try {
      // Test the actual REST API endpoint
      const response = await fetch(`${url}/rest/v1/user?limit=1`, {
        headers: {
          'apikey': key,
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json'
        }
      });
      
      const connected = response.ok || response.status === 200 || response.status === 204;
      console.log(`  ${connected ? '✅ Connected' : '❌ Not Connected'} (Status: ${response.status})`);
      console.log(`  URL: ${url.replace(/https:\/\/(.+?)\.supabase\.co.*/, '$1.supabase.co')}`);
      
      // Check if tables exist
      if (connected) {
        try {
          const data = await response.json();
          console.log(`  Tables: ${Array.isArray(data) ? '✅ Accessible' : '⚠️ Check schema'}`);
        } catch {
          console.log('  Tables: ✅ API responding');
        }
      }
      
      return { configured: true, connected, status: response.status };
    } catch (e) {
      console.log('  ❌ Connection Error:', e.message);
      console.log('  Tip: Check if Supabase project is active');
      return { configured: true, connected: false, error: e.message };
    }
  },

  // Test Hume Voice
  testHume() {
    const apiKey = process.env.REACT_APP_HUME_API_KEY;
    const configId = process.env.REACT_APP_HUME_CONFIG_ID;
    const voiceActive = localStorage.getItem('hume_voice_active') === 'true';
    
    console.log('\n🎤 HUME VOICE:');
    console.log(`  API Key: ${apiKey ? '✅ Configured' : '❌ Missing'}`);
    console.log(`  Config ID: ${configId ? '✅ Configured' : '❌ Missing'}`);
    console.log(`  Voice Active: ${voiceActive ? '✅ Yes' : '❌ No'}`);
    
    return { 
      configured: !!(apiKey && configId),
      active: voiceActive
    };
  },

  // Test routing
  testRouting() {
    const path = window.location.pathname;
    const validPaths = [
      '/dashboard/patient',
      '/dashboard/clinician',
      '/dashboard/superadmin',
      '/apple', '/boxing', '/potato', '/row', '/sky', '/tomato', '/garden', '/berry', '/kick', '/skate'
    ];
    
    const isValid = validPaths.some(p => path.includes(p)) || path === '/';
    
    console.log('\n🛣️ ROUTING:');
    console.log(`  Current Path: ${path}`);
    console.log(`  Valid Route: ${isValid ? '✅ Yes' : '❌ No'}`);
    
    return { path, isValid };
  },

  // Test mock data
  testMockData() {
    // Check if mock data is being used
    const checks = {
      patients: !!window.mockPatients || document.querySelector('.patient-card'),
      clinicians: !!window.mockClinicians,
      messages: !!window.mockMessages || document.querySelector('.chat-message')
    };
    
    console.log('\n📦 MOCK DATA:');
    console.log(`  Using Mock: ${Object.values(checks).some(v => v) ? '✅ Yes' : '❌ No'}`);
    
    return checks;
  },

  // Print final summary
  printSummary(results) {
    console.log('\n' + '='.repeat(60));
    console.log('%c📊 CONNECTION STATUS SUMMARY', 'font-size: 16px; font-weight: bold; color: #6D4F79');
    console.log('='.repeat(60));
    
    // Determine overall status
    const isFullyConnected = results.supabase?.connected && !results.auth?.authBypassed;
    const isMockMode = results.auth?.authBypassed || !results.supabase?.configured;
    const isPartiallyConnected = results.supabase?.configured && !results.supabase?.connected;
    
    if (isMockMode) {
      console.log('%c📦 MODE: FRONTEND ONLY (Mock Data)', 'font-size: 14px; color: orange; font-weight: bold');
      console.log('   ✅ All dashboards working with mock data');
      console.log('   ⚠️ Database not connected');
      console.log('   ⚠️ Authentication bypassed for testing');
    } else if (isFullyConnected) {
      console.log('%c✅ MODE: FULLY CONNECTED', 'font-size: 14px; color: green; font-weight: bold');
      console.log('   ✅ Database connected');
      console.log('   ✅ Authentication enabled');
      console.log('   ✅ Real data persistence');
    } else if (isPartiallyConnected) {
      console.log('%c⚠️ MODE: PARTIALLY CONNECTED', 'font-size: 14px; color: #FFA500; font-weight: bold');
      console.log('   ⚠️ Database configured but not connected');
      console.log('   ⚠️ Check Supabase credentials');
    } else {
      console.log('%c❌ MODE: NOT CONFIGURED', 'font-size: 14px; color: red; font-weight: bold');
      console.log('   ❌ Missing configuration');
      console.log('   ❌ Add Supabase credentials to .env');
    }
    
    // Quick actions
    console.log('\n💡 QUICK ACTIONS:');
    console.log('1. Enable Auth: Uncomment lines 138-140 in App.js');
    console.log('2. Connect DB: Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_KEY to .env');
    console.log('3. Test Login: Use sarah/pass123 (clinician) or 847392 (patient)');
    console.log('4. Clear Cache: localStorage.clear()');
    console.log('5. Re-run Test: QuickConnectionTest.runAll()');
    
    // Test specific features
    console.log('\n🧪 TEST FEATURES:');
    console.log('• Create Patient: Go to /dashboard/clinician → Click "Create New Patient"');
    console.log('• Send Message: Go to /dashboard/superadmin → Click patient → "Message"');
    console.log('• Play Game: Go to /dashboard/patient → Click any game card');
    
    return {
      mode: isMockMode ? 'mock' : isFullyConnected ? 'connected' : isPartiallyConnected ? 'partial' : 'not_configured',
      timestamp: results.timestamp
    };
  }
};

// Auto-attach to window for console access
if (typeof window !== 'undefined') {
  window.QuickConnectionTest = QuickConnectionTest;
  console.log('%c✨ QuickConnectionTest loaded!', 'color: green; font-weight: bold');
  console.log('Run: QuickConnectionTest.runAll()');
}

export default QuickConnectionTest;